import React from 'react';
import styled from 'styled-components';
import { Form } from 'react-bootstrap';

import type { IFormSelect } from './types';

import { SelectLabel } from '@/components/common';
import { Fieldset, InputDescription, InputErrorMessage } from '@/components/common/form/components';
import { classList } from '@/helpers';

const Select = (props: IFormSelect): React.ReactElement => {
  const {
    className,
    children,
    options = [],
    id,
    label,
    required,
    register,
    error,
    touched,
    name,
    placeholder,
    placeholderValue,
    placeholderDisabled,
    isStandalone,
    showError,
    ...rest
  } = props;

  const registerData = register && register(name, { required: required });

  return (
    <StyledFieldset className={className}>
      {label && (
        <SelectLabel htmlFor={name}>
          {required && <span className="text-danger me-1">*</span>}
          {label}
        </SelectLabel>
      )}
      <StyledSelect as="select" id={id || name} className={classList(error && 'error')} {...registerData} {...rest}>
        {placeholder && (
          <option value={placeholderValue ?? ('' || 0)} disabled={placeholderDisabled}>
            {placeholder || '-'}
          </option>
        )}
        {children ||
          options.map(option => (
            <option key={`${name}-${option.value}`} value={option.value}>
              {option.label}
            </option>
          ))}
      </StyledSelect>
      {rest.description && <InputDescription>{rest.description}</InputDescription>}
      {!isStandalone && <InputErrorMessage showError={showError} error={error} touched={touched} />}
    </StyledFieldset>
  );
};

export { Select };

const StyledSelect = styled(Form.Control).attrs({
  className: `
    px-3 py-1 w-100
    border rounded-3 styled-select
  `,
})`
  margin-top: 3px;
  color: #343a40 !important;
  background-color: #ffffff !important;
  height: ${props => props?.height || '46px'};
  padding-right: 30px !important;
  appearance: none;
  border-radius: 8px !important;

  background-image: linear-gradient(45deg, transparent 50%, gray 50%),
    linear-gradient(135deg, gray 50%, transparent 50%), linear-gradient(to right, #ccc, #ccc);
  background-position: calc(100% - 20px) calc(1em + 4px), calc(100% - 16px) calc(1em + 4px), calc(100% - 2.5em) 0.5em;
  background-size: 5px 5px, 5px 5px, 0px 1.5em;
  background-repeat: no-repeat;

  &.error {
    border: 1px solid #dc3545 !important;
  }

  &:disabled {
    background-color: #e9ecef !important;
    color: #868e96 !important;
    opacity: 1;
  }
`;

const StyledFieldset = styled(Fieldset)`
  margin-bottom: 6px;
`;
